import { changed, noOp, rejected, type Command, type CommandExecution } from './commands';
import { damage } from './damage';
import { findLayer, type EditorDocument, type Layer } from './model';

export const LAYER_STYLE_EXTENSION = 'maipo:layer-style';

export interface LayerStyle {
  dropShadow?: {
    enabled: boolean;
    color: string;
    opacity: number;
    blur: number;
    offsetX: number;
    offsetY: number;
  };
  stroke?: {
    enabled: boolean;
    color: string;
    opacity: number;
    width: number;
  };
}

export function layerStyle(layer: Layer): LayerStyle {
  const value = layer.extensions[LAYER_STYLE_EXTENSION];
  if (!value || typeof value !== 'object') return {};
  return structuredClone(value) as LayerStyle;
}

export class UpdateLayerStyleCommand implements Command {
  readonly label = 'Change layer style';
  private before?: unknown;
  private hadBefore = false;

  constructor(private readonly layerId: string, private readonly style: LayerStyle) {}

  retainedBytes(): number {
    return 128 + JSON.stringify(this.style).length * 2 + JSON.stringify(this.before ?? null).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return rejected('Layer no longer exists.');
    if (JSON.stringify(layerStyle(layer)) === JSON.stringify(this.style)) return noOp();
    if (this.before === undefined) {
      this.hadBefore = Object.hasOwn(layer.extensions, LAYER_STYLE_EXTENSION);
      this.before = structuredClone(layer.extensions[LAYER_STYLE_EXTENSION] ?? null);
    }
    if (Object.keys(this.style).length === 0) delete layer.extensions[LAYER_STYLE_EXTENSION];
    else layer.extensions[LAYER_STYLE_EXTENSION] = structuredClone(this.style);
    return changed(damage({ fullDocument: true, layerIds: [this.layerId] }));
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return;
    if (this.hadBefore) layer.extensions[LAYER_STYLE_EXTENSION] = structuredClone(this.before);
    else delete layer.extensions[LAYER_STYLE_EXTENSION];
  }
}
